import localForage from 'localforage';
import { persistReducer } from 'redux-persist';
import { type PeopleState, type PeopleActionTypes } from 'app/store/types';
import { createSelector } from '@reduxjs/toolkit';
import { groupByID } from 'app/utils/helpers';
import { type RootState } from 'app/store';

const initialState: PeopleState = {
	jobs: undefined!, // HACK::assume never undefined and handle check only in <LicenseGroupWrapper /> and <ToolbarLayoutAdmin /> (actually it's a little more complicated - there's two ways to call this but the existing `getUserJobs` falls back to an empty object)
	collabJob: null,
	queues: {},
	tenantQueues: {},
	queueJobs: {},
	selectedJob: null,
	inbox: {},
	tracker: {},
	dropData: {},
};

const peopleReducer = (state = initialState, action: PeopleActionTypes) => {
	switch (action.type) {
		case 'GET_USER_JOBS_SUCCESS': {
			return {
				...state,
				jobs: action.payload,
			};
		}
		case 'GET_LICENSE_GROUP_DATA_SUCCESS': {
			const {
				data: { jobs },
			} = action.payload;

			return {
				...state,
				...(jobs && { jobs }),
			};
		}
		case 'GET_COLLAB_JOB_RECORD_SUCCESS': {
			return {
				...state,
				collabJob: action.payload,
			};
		}
		case 'RESET_COLLAB_JOB_RECORD_SUCCESS': {
			return {
				...state,
				collabJob: null,
			};
		}
		case 'GET_SELECTED_JOB_SUCCESS': {
			return {
				...state,
				selectedJob: action.payload,
			};
		}
		case 'RESET_SELECTED_JOB_SUCCESS': {
			return {
				...state,
				selectedJob: null,
			};
		}
		case 'GET_USER_QUEUES_SUCCESS': {
			return {
				...state,
				queues: {
					...action.payload,
				},
			};
		}
		case 'GET_TENANT_QUEUES_SUCCESS': {
			return {
				...state,
				tenantQueues: {
					...action.payload,
				},
			};
		}
		case 'GET_QUEUE_JOBS_SUCCESS': {
			return {
				...state,
				queueJobs: {
					...state.queueJobs,
					[action.payload.id]: action.payload.jobs,
				},
			};
		}
		case 'GET_USER_INBOX_SUCCESS': {
			return {
				...state,
				inbox: action.payload,
			};
		}
		case 'ACCEPT_JOB_SUCCESS': {
			return {
				...state,
				...(state.selectedJob && {
					selectedJob: {
						...state.selectedJob,
						accepted: true,
					},
				}),
			};
		}
		case 'GET_TRACKER_SUCCESS': {
			return {
				...state,
				tracker: action.payload,
			};
		}
		case 'TRACK_JOB_SUCCESS': {
			return {
				...state,
				tracker: action.payload,
			};
		}
		case 'UNTRACK_JOB_SUCCESS': {
			return {
				...state,
				tracker: action.payload,
			};
		}
		case 'GET_DROP_DATA_SUCCESS': {
			return {
				...state,
				dropData: action.payload,
			};
		}
		case 'LOGGED_OUT_USER':
		case 'PURGE_STATE':
			return initialState;
		default: {
			return state;
		}
	}
};

// reducer
// const selectedFormReducer = (state: { [licenseGroupId: string]: Form } = {}, action: any) => {
// 	switch (action.type) {
// 		case GET_SELECTED_FORM_SUCCESS:
// 			return {
// 				...state,
// 				[action.payload.licenseGroupId]: action.payload.form
// 			};
// 		// ... other cases ...
// 		default:
// 			return state;
// 	}
// };

export default persistReducer(
	{
		key: 'people',
		storage: localForage,
	},
	peopleReducer,
);

// Selectors
const EMPTY_OBJECT = {};
const getUserJobs = ({ people }: RootState) => people.jobs ?? EMPTY_OBJECT;
const getCollabJob = ({ people }: RootState) => people.collabJob;
const getSelectedJob = ({ people }: RootState) => people.selectedJob;
// const getVisibleJobs = ({ people }: RootState) => ({
// 	...(people.jobs ?? EMPTY_OBJECT),
// 	...Object.values(people.queueJobs).reduce(
// 		(prev, queue) => ({
// 			...prev,
// 			...queue,
// 		}),
// 		{},
// 	),
// });
const getUserQueues = ({ people }: RootState) => people.queues;
const getTenantQueues = ({ people }: RootState) => people.tenantQueues;
const getQueueJobs = ({ people }: RootState) => people.queueJobs;
const getUserInbox = ({ people }: RootState) => people.inbox;
const getJobTracker = ({ people }: RootState) => people.tracker;
const getDropData = ({ people }: RootState) => people.dropData;
export const getUserJobsSelector = createSelector([getUserJobs], (userJobs) => userJobs);
// export const getArchivedJobs = createSelector([getUserJobs], (userJobs) => userJobs); // CHANGEME::this should filter to the WFX ACL jobs
export const getCollabJobsSelector = createSelector([getCollabJob], (collabJob) => collabJob);
export const getSelectedJobSelector = createSelector([getSelectedJob], (selectedJob) => selectedJob);
// export const getVisibleJobsSelector = createSelector([getVisibleJobs], (visibleJobs) => visibleJobs);
export const getUserQueuesSelector = createSelector([getUserQueues], (queues) => queues);
export const getActiveUserQueuesSelector = createSelector([getUserQueues], (queues) =>
	groupByID(Object.values(queues).filter((q) => q.active)),
);
export const getTenantQueuesSelector = createSelector([getTenantQueues], (tenantQueues) => tenantQueues);
export const getSelectedQueueJobs = (id: string) => createSelector([getQueueJobs], (queues) => queues[id]);
export const getUserInboxSelector = createSelector([getUserInbox], (inbox) => inbox);
export const getTrackedJobs = createSelector([getJobTracker], (tracker) => tracker);
export const getIsJobTracked = (id: string) => createSelector([getJobTracker], (tracker) => tracker[id] !== undefined);
export const getDropDataSelector = createSelector([getDropData], (dropData) => dropData);
